import { DocumentNode, gql } from '@apollo/client';
import {
  Box,
  HStack,
  IconButton,
  LinkBox,
  LinkOverlay,
  StackProps,
  Text,
  VStack,
} from '@chakra-ui/react';
import dynamic from 'next/dynamic';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import * as React from 'react';

import { PreviewRendererEventProps } from '@/components/PreviewRendererEvent/PreviewRendererEvent';
import { PREVIEW_RENDERER_VERSION_FILE_FRAGMENT } from '@/components/PreviewRenderer/PreviewRendererFragment';
import {
  DownloadIcon,
  ImageIcon,
  PaperclipIcon,
} from '@/imports/ui/chakra/feather';

import { AttachmentDisplayEvent_VersionFileFragment } from './graphql-types/AttachmentDisplayEvent_VersionFileFragment';

/* we can't load parts of react-dom-preview on the server  */
const PreviewRendererModalEvent = dynamic<PreviewRendererEventProps>(
  () =>
    import('@/components/PreviewRendererEvent').then((mod) => mod.PreviewRendererEvent),
  {
    ssr: false,
  }
);

type IAttachmentDisplayEventProps = {
  versionFiles: (AttachmentDisplayEvent_VersionFileFragment | null)[] | null;
} & StackProps;

export const AttachmentDisplayEvent: React.FC<IAttachmentDisplayEventProps> & {
  versionFileFragment: DocumentNode;
} = ({ versionFiles, ...stackProps }) => {
  if (!versionFiles || versionFiles.length === 0) {
    return null;
  }
  return (
    <VStack width="full" spacing="2" {...stackProps}>
      {versionFiles
        .filter(
          (
            versionFile
          ): versionFile is AttachmentDisplayEvent_VersionFileFragment => {
            return !!versionFile;
          }
        )
        .map((versionFile) => {
          return (
            <IndividualAttachmentDisplayEvent
              key={versionFile._id}
              versionFile={versionFile}
            />
          );
        })}
    </VStack>
  );
};

const IndividualAttachmentDisplayEvent: React.FC<{
  versionFile: AttachmentDisplayEvent_VersionFileFragment;
}> = ({ versionFile }) => {
  const router = useRouter();

  const href = {
    pathname: router.pathname,
    query: {
      ...router.query,
      eventVersionFileId: versionFile._id.toString(),
    },
  };

  return (
    <>
      <PreviewRendererModalEvent
        versionFile={versionFile}
        onClose={() => {
          const { eventVersionFileId, ...query } = router.query;
          router.replace(
            { pathname: router.pathname, query: query },
            undefined,
            { shallow: true }
          );
        }}
        isOpen={router.query.eventVersionFileId === versionFile._id}
      />
      <LinkBox width="full">
        <Box
          p="4"
          borderStyle="solid"
          borderColor="gray.200"
          borderWidth="1px"
          borderRadius="xl"
          width="full"
        >
          <HStack width="full">
            {versionFile.file?.isVideo || versionFile.file?.isImage ? (
              <ImageIcon />
            ) : (
              <PaperclipIcon />
            )}
            <NextLink href={href} shallow={false} scroll={false} passHref legacyBehavior>
              <LinkOverlay flex="1">
                <Text as="span" fontWeight="semibold">
                  {versionFile.file?.name || 'No name available'}
                </Text>
              </LinkOverlay>
            </NextLink>
            <NextLink
              href={versionFile.file?.url || ''}
              download={versionFile.file?.name}
            >
              <IconButton
                variant="ghost"
                colorScheme="gray"
                icon={<DownloadIcon color="gray.500" />}
                aria-label={`download ${versionFile.file?.name}`}
              />
            </NextLink>
          </HStack>
        </Box>
      </LinkBox>
    </>
  );
};

AttachmentDisplayEvent.versionFileFragment = gql`
  fragment AttachmentDisplayEvent_VersionFileFragment on VersionFile {
    _id
    file {
      _id
      name
      isVideo
      isImage
      url
    }
    ...PreviewRenderer_VersionFileFragment
  }
  ${PREVIEW_RENDERER_VERSION_FILE_FRAGMENT}
`;
